import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/Navbar";
import SignInModal from "../../components/SignInModal";
import { CHANGELOG_ENTRIES } from "./articles";

const BRAND_COLOR = "#1a3f6b";
const BRAND_ACCENT_COLOR = "#10b981";


export default function Changelog() {
  const [showSignIn, setShowSignIn] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <div style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", background: "#f9fafb", minHeight: "100vh" }}>
      <Navbar onSignIn={() => setShowSignIn(true)} />

      {/* Hero */}
      <header
        style={{
          background: "linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #134e4a 100%)",
          padding: isMobile ? "5rem 1rem 3rem" : "7rem 2rem 4rem",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)", backgroundSize: "40px 40px", pointerEvents: "none" }} />
        {/* Floating glow orbs */}
        <div style={{ position: "absolute", top: "-20%", right: "-10%", width: "400px", height: "400px", borderRadius: "50%", background: "radial-gradient(circle, rgba(232,116,12,0.12) 0%, transparent 70%)", pointerEvents: "none", animation: "changelogPulse 6s ease-in-out infinite" }} />
        <div style={{ position: "absolute", bottom: "-25%", left: "-8%", width: "450px", height: "450px", borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)", pointerEvents: "none", animation: "changelogPulse 7s ease-in-out 1.5s infinite" }} />
        <style>{`@keyframes changelogPulse { 0%, 100% { transform: scale(1); opacity: 0.7; } 50% { transform: scale(1.15); opacity: 1; } }`}</style>
        <div style={{ maxWidth: "780px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem", padding: "0.35rem 1rem", background: "rgba(232,116,12,0.15)", border: "1px solid rgba(232,116,12,0.3)", borderRadius: "999px", fontSize: "0.8rem", fontWeight: 600, color: "#f59e0b", marginBottom: "1.25rem", letterSpacing: "0.04em", textTransform: "uppercase" }}>
            <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#f59e0b", display: "inline-block" }} />
            Release History
          </div>
          <h1 style={{ fontSize: isMobile ? "2.25rem" : "3.25rem", fontWeight: 800, color: "#fff", lineHeight: 1.1, marginBottom: "1rem", letterSpacing: "-0.03em" }}>
            Changelog
          </h1>
          <p style={{ fontSize: isMobile ? "1.05rem" : "1.2rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.6, maxWidth: "540px", margin: "0 auto" }}>
            Everything we've shipped — new features, major updates, and improvements.
          </p>
        </div>
      </header>

      {/* Timeline */}
      <main style={{ maxWidth: "820px", margin: "0 auto", padding: isMobile ? "2rem 1rem" : "3rem 2rem 4rem" }}>
        {CHANGELOG_ENTRIES.map((release, ri) => (
          <section key={ri} style={{ marginBottom: "3.5rem" }}>
            {/* Release header */}
            <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: "1rem", marginBottom: "1.5rem", flexDirection: isMobile ? "column" : "row" }}>
              <div
                style={{
                  padding: "0.35rem 1rem",
                  background: BRAND_COLOR,
                  color: "#fff",
                  borderRadius: "999px",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                {release.date}
              </div>
              <h2 style={{ fontSize: "1.35rem", fontWeight: 700, color: BRAND_COLOR, margin: 0 }}>
                {release.version}
              </h2>
              <Link
                to={`/blog/${release.slug}`}
                style={{
                  fontSize: "0.85rem",
                  color: BRAND_ACCENT_COLOR,
                  fontWeight: 600,
                  textDecoration: "none",
                  marginLeft: isMobile ? 0 : "auto",
                  whiteSpace: "nowrap",
                }}
              >
                Read full post →
              </Link>
            </div>

            {/* Vertical timeline line + items */}
            <div style={{ position: "relative", paddingLeft: "1.75rem" }}>
              {/* Vertical line */}
              <div
                style={{
                  position: "absolute",
                  left: "6px",
                  top: "4px",
                  bottom: "4px",
                  width: "2px",
                  background: "#e5e7eb",
                  borderRadius: "999px",
                }}
              />

              {release.items.map((item, ii) => (
                <div key={ii} style={{ position: "relative", marginBottom: ii === release.items.length - 1 ? 0 : "1.25rem" }}>
                  {/* Dot */}
                  <div
                    style={{
                      position: "absolute",
                      left: "-1.75rem",
                      top: "6px",
                      width: "14px",
                      height: "14px",
                      borderRadius: "50%",
                      background: item.badgeColor,
                      border: "3px solid #f9fafb",
                    }}
                  />

                  {/* Card */}
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "12px",
                      padding: "1rem 1.25rem",
                      transition: "box-shadow 0.15s ease",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.06)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "0.4rem", flexWrap: "wrap" }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "0.15rem 0.6rem",
                          fontSize: "0.7rem",
                          fontWeight: 700,
                          color: "#fff",
                          background: item.badgeColor,
                          borderRadius: "999px",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                        }}
                      >
                        {item.badge}
                      </span>
                      <span style={{ fontSize: "1rem", fontWeight: 600, color: "#111827" }}>
                        {item.title}
                      </span>
                    </div>
                    <p style={{ fontSize: "0.9rem", color: "#6b7280", lineHeight: 1.6, margin: 0 }}>
                      {item.description}
                    </p>
                    {item.screenshot && (
                      <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem", marginTop: "0.75rem" }}>
                        <div
                          onClick={() => setPreviewImage({ src: item.screenshot, alt: item.title })}
                          style={{
                            display: "inline-block",
                            cursor: "zoom-in",
                            borderRadius: "6px",
                            overflow: "hidden",
                            border: "1px solid #e5e7eb",
                            transition: "box-shadow 0.15s ease",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 2px 12px rgba(0,0,0,0.12)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
                        >
                          <img
                            src={item.screenshot}
                            alt={item.title}
                            loading="lazy"
                            style={{ width: "120px", height: "auto", display: "block" }}
                          />
                        </div>
                        <button
                          onClick={() => setPreviewImage({ src: item.screenshot, alt: item.title })}
                          style={{
                            background: "none",
                            border: "none",
                            padding: 0,
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            color: "#0a9fa6",
                            fontWeight: 600,
                            fontFamily: "inherit",
                            display: "flex",
                            alignItems: "center",
                            gap: "0.25rem",
                            marginBottom: "0.15rem",
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.textDecoration = "underline"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.textDecoration = "none"; }}
                        >
                          View full image ↗
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>

      {showSignIn && <SignInModal show={showSignIn} onHide={() => setShowSignIn(false)} />}

      {/* Image preview lightbox */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
            padding: "2rem",
          }}
        >
          <img
            src={previewImage.src}
            alt={previewImage.alt}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              borderRadius: "12px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.4)",
            }}
          />
        </div>
      )}
    </div>
  );
}
